import { queryOptions, skipToken } from '@tanstack/react-query';
import {
  WORKBOOK_REPORT_DATA_MAX_REQUESTS,
  type WorkbookAggregateInput,
  type WorkbookAggregateResult,
  type WorkbookReportData,
  type WorkbookReportDataInput,
  type WorkbookReportDataRequest,
  type WorkbookResultRows,
  type WorkbookResultRowsInput,
} from '@erp/shared-types';
import { orpc, rpcClient } from '@/api/rpc/client';

type Answer = WorkbookReportData[number];
type Pending = {
  request: WorkbookReportDataRequest;
  resolve: (answer: Answer) => void;
  reject: (error: unknown) => void;
};

/**
 * How long reads wait for the others a Report makes as it renders: its
 * charts ask in the same render, so a few milliseconds gathers them all.
 */
const BATCH_WINDOW_MS = 8;

/**
 * Gathers the reads a Report's components make together into one Report data
 * request per Workbook (at most the request cap each), so the server reads
 * and aggregates each Result once for all of them. Each read resolves with
 * its own answer, or rejects with its own error.
 */
export function createReportDataBatcher(
  send: (input: WorkbookReportDataInput) => Promise<WorkbookReportData>,
) {
  const queued = new Map<string, Pending[]>();
  function flush(workbookId: string) {
    const pending = queued.get(workbookId) ?? [];
    queued.delete(workbookId);
    for (
      let start = 0;
      start < pending.length;
      start += WORKBOOK_REPORT_DATA_MAX_REQUESTS
    ) {
      const batch = pending.slice(
        start,
        start + WORKBOOK_REPORT_DATA_MAX_REQUESTS,
      );
      send({ workbookId, requests: batch.map((item) => item.request) }).then(
        (answers) =>
          batch.forEach((item, index) => {
            const answer = answers[index];
            if (!answer) item.reject(new Error('No answer for this chart.'));
            else if (answer.kind === 'error')
              item.reject(new Error(answer.message));
            else item.resolve(answer);
          }),
        (error: unknown) => batch.forEach((item) => item.reject(error)),
      );
    }
  }
  return function load(
    workbookId: string,
    request: WorkbookReportDataRequest,
  ): Promise<Answer> {
    return new Promise<Answer>((resolve, reject) => {
      const pending = queued.get(workbookId);
      if (pending) pending.push({ request, resolve, reject });
      else {
        queued.set(workbookId, [{ request, resolve, reject }]);
        setTimeout(() => flush(workbookId), BATCH_WINDOW_MS);
      }
    });
  };
}

const loadReportData = createReportDataBatcher((input) =>
  rpcClient.workbooks.reportData(input),
);

/**
 * A chart's Server aggregation, read in its Report's batch. Keyed as the
 * `aggregate` procedure's query, so what waits on Report reads sees it.
 */
export function reportAggregateQueryOptions(
  input: WorkbookAggregateInput | typeof skipToken,
) {
  return queryOptions({
    queryKey: orpc.workbooks.aggregate.queryKey({
      input: input === skipToken ? skipToken : input,
    }),
    queryFn:
      input === skipToken
        ? skipToken
        : async (): Promise<WorkbookAggregateResult> => {
            const { workbookId, ...target } = input;
            const answer = await loadReportData(workbookId, {
              kind: 'aggregate',
              ...target,
            });
            if (answer.kind !== 'aggregate')
              throw new Error('Expected an aggregation.');
            return answer.result;
          },
    // A Run's Result never changes.
    staleTime: Infinity,
  });
}

/** A Table's leading rows, read in its Report's batch; keyed as the `resultRows` query. */
export function reportLeadingRowsQueryOptions(
  input: (WorkbookResultRowsInput & { limit: number }) | typeof skipToken,
) {
  return queryOptions({
    queryKey: orpc.workbooks.resultRows.queryKey({
      input: input === skipToken ? skipToken : input,
    }),
    queryFn:
      input === skipToken
        ? skipToken
        : async (): Promise<WorkbookResultRows> => {
            const { workbookId, ...target } = input;
            const answer = await loadReportData(workbookId, {
              kind: 'rows',
              ...target,
            });
            if (answer.kind !== 'rows') throw new Error('Expected rows.');
            return answer.result;
          },
    staleTime: Infinity,
  });
}
